import type { AccountInfo, IPublicClientApplication } from '@azure/msal-browser'
import createClient, { type Client, type Middleware } from 'openapi-fetch'
import { apiScopes } from '../auth'
import type { AppConfig } from '../config'
import type { paths } from '@aurigaengineering/contracts-admin'

export type ApiClient = Client<paths>

/**
 * The API, typed by the contract.
 *
 * The types come from the contract package, generated from the API's own emitted OpenAPI
 * document and never written here (contracts:R06, R07).
 *
 * The base URL comes from the runtime configuration, not the build: one artefact is promoted
 * from Development to Production, so a baked-in API address would be the promotion model
 * broken in the most ordinary way there is.
 */
export function createApi(config: AppConfig, instance: IPublicClientApplication): ApiClient {
  const client = createClient<paths>({ baseUrl: config.apiBase })

  client.use(bearer(config, instance))

  return client
}

/**
 * Puts the access token on every request.
 *
 * Silently: MSAL serves it from its own cache until it expires, so this is a lookup rather
 * than a round trip. A request with no signed-in account goes out bare, which is what makes
 * the API's 401 the thing the screen reacts to -- the alternative is a client that throws on
 * its own before the API has said anything.
 */
function bearer(config: AppConfig, instance: IPublicClientApplication): Middleware {
  return {
    async onRequest({ request }) {
      const account: AccountInfo | null = instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null

      if (!account) {
        return request
      }

      const token = await instance.acquireTokenSilent({ scopes: apiScopes(config), account })
      request.headers.set('Authorization', `Bearer ${token.accessToken}`)

      return request
    },
  }
}
